import { ArrowRight2 } from 'iconsax-react';
import { isNil } from 'lodash-es';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useTypedParams, useTypedState } from 'react-router-typesafe-routes';
import DashedCircleAddIcon from '@/assets/images/icons/dashed-circle-add.svg?react';
import AppCell from '@/components/common/app-cell';
import CopyButton from '@/components/common/copy-button';
import Linkable from '@/components/common/linkable';
import { SafeImage } from '@/components/common/safe-image';
import Toggle from '@/components/common/toggle';
import { UserCarousel } from '@/components/common/user-carousel/user-carousel';
import { GroupAvatarUploadButton } from '@/components/groups/group-avatar-upload-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useClearSession from '@/hooks/conversations/use-clear-session';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useGroupMembers from '@/hooks/groups/use-group-members';
import useQuitGroup from '@/hooks/groups/use-quit-group';
import useUpdateGroupDetails from '@/hooks/groups/use-update-group-details';
import useSessionSettings from '@/hooks/sessions/use-session-settings';
import useUpdateSessionDnd from '@/hooks/sessions/use-update-session-dnd';
import useUpdateSessionTop from '@/hooks/sessions/use-update-session-top';
import { useIsDesktop } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { makePrompt } from '@/stores/prompter';
import copyTextToClipboard from '@/utils/copy-text';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';

export function GroupSettingHeader({ groupId }: { groupId: number }) {
  const { data: groupInfo, canManage } = useGroupInfo(groupId);

  return (
    <div className='flex gap-6 items-stretch mx-4'>
      <GroupAvatarUploadButton
        canManage={canManage}
        groupId={groupId}>
        <SafeImage
          src={groupInfo?.GroupData?.GroupImg}
          size={[80, 80]}
          className='size-20 rounded-full bg-cover'
        />
      </GroupAvatarUploadButton>

      <Linkable
        linkable={canManage}
        className='flex flex-1 gap-3 items-center'
        to={ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.INFO.$buildPath({ params: { id: groupId } })}>
        <h2 className='line-clamp-1 break-all flex-1 font-bold'>{groupInfo?.GroupData?.GroupName}</h2>

        <ArrowRight2
          variant='Outline'
          className='size-6'
        />
      </Linkable>
    </div>
  );
}

export default function GroupSettingsByIdIndexRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS);
  const { from: navigatedFrom } = useTypedState(ROUTES.MESSAGES.GROUPS.SETTINGS);
  const isDesktop = useIsDesktop();

  const { data: groupInfo, isGroupOwner, isChannel, canManage, isPublicGroup } = useGroupInfo(groupId);
  const { memberCount } = useGroupMembers(groupId);
  const { session: groupSession, refetch } = useSessionSettings('group', groupId);
  const { mutateAsync: quitGroup } = useQuitGroup(groupId, navigatedFrom === 'comm');
  const { mutateAsync: updateGroup } = useUpdateGroupDetails(groupId);
  const { mutateAsync: setDoNotDisturb, isPending: isUpdatingDnd } = useUpdateSessionDnd('group', groupId);
  const { clearSession } = useClearSession('group', groupId);
  const { mutateAsync: updateSessionTop, isPending: isUpdatingSessionTop } = useUpdateSessionTop('group', groupId);

  const handleClearMessages = () =>
    makePrompt({
      key: 'clear-group-messages',
      type: 'dialog',
      title: t('conversation.setting.group.clear-message.title'),
      content: t('conversation.setting.group.clear-message.content'),
      promise: async () => {
        await clearSession(false);
        useConversationSessionStore
          .getState()
          .setLastReadNotice(generateSessionKey('group', groupId), groupInfo?.GroupData?.Info);
      },
    }).catch(console.error);

  const handleQuitGroup = () =>
    makePrompt({
      key: 'quit-group',
      type: 'dialog',
      title: isGroupOwner
        ? t('conversation.setting.group.quit.admin-title')
        : t('conversation.setting.group.quit.title'),
      resolveButtonLabel: isGroupOwner
        ? t('conversation.setting.group.quit.admin-ok')
        : t('conversation.setting.group.quit.ok'),
      promise: async () => {
        const result = await quitGroup();

        if (result.Code === 0) {
          navigate(ROUTES.MESSAGES.$path());
        }
      },
    });

  const handleUpdateJoinType = (e: MouseEvent) => {
    const { bottom = 0, left = 0 } = e.currentTarget.getBoundingClientRect();

    return makePrompt({
      key: `entry-group-method'`,
      type: 'action-sheet',
      title: '',
      meta: {
        position: [left, bottom],
      },
      resolutionButtons: [
        {
          label: t('conversation.setting.group.public-group'),
          resolution: '0',
        },
        {
          label: t('conversation.setting.group.private-group'),
          resolution: '1',
        },
      ],
      promise: (resolution) => {
        if (resolution === '0' || resolution === '1') {
          return updateGroup({ JoinType: Number(resolution) as 0 | 1 });
        }
      },
    });
  };

  return (
    <PageLayout
      className='desktop:h-fit'
      title={t('conversation.setting.group.title')}
      headerConfig={{
        navigateBack: true,
        className: 'desktop:hidden',
      }}>
      <div className='mt-4 desktop:mt-0 pb-safe-offset-4 flex-1 overflow-y-auto flex flex-col basis-50dvh justify-start'>
        {!isDesktop && !isChannel && (
          <>
            <UserCarousel
              className='mx-5'
              users={
                groupInfo?.Members?.map((member) => ({
                  userId: member.UserID,
                  groupId: member.GroupID,
                  avatarUrl: member.UserHeadImg,
                  label: member.GroupMemberName || member.UserNickName,
                  defaultAvatar: 'user',
                })) ?? []
              }>
              <Link
                to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.MEMBERS.$path({ relative: true })}
                className='rounded-full'>
                <DashedCircleAddIcon className='size-12' />
              </Link>
            </UserCarousel>

            <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.MEMBERS.$path({ relative: true })}>
              <AppCell
                label={t('conversation.setting.group.group-member')}
                content={t('conversation.setting.group.member-count', { count: memberCount })}
                chevron
              />
            </Link>

            <hr className='border-surface border-t desktop:mx-0 mx-4 my-2' />
          </>
        )}

        {canManage && !isChannel && (
          <>
            <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.ADMIN.$path({ relative: true })}>
              <AppCell
                label={t('conversation.setting.group.management')}
                chevron={!isDesktop}
              />
            </Link>

            <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.ADMIN.APPLICATIONS.$path({ relative: true })}>
              <AppCell
                label={t('conversation.setting.group.applications')}
                content={
                  !isNil(groupInfo?.GroupJoinApplyCount) &&
                  groupInfo.GroupJoinApplyCount > 0 && (
                    <span
                      className={cn(
                        'badge badge-accent bg-red-500 text-neutral-0 border-0 badge-xs size-4 text-[10px]',
                        groupInfo.GroupJoinApplyCount > 99 && 'w-6',
                      )}>
                      {groupInfo.GroupJoinApplyCount > 99 ? '99+' : groupInfo.GroupJoinApplyCount}
                    </span>
                  )
                }
                chevron={!isDesktop}
              />
            </Link>

            <AppCell
              label={t('conversation.setting.group.entry-method')}
              content={t(
                isPublicGroup ? 'conversation.setting.group.public-group' : 'conversation.setting.group.private-group',
              )}
              chevron={!isDesktop}
              onClick={handleUpdateJoinType}
            />

            <hr className='border-surface border-t desktop:mx-0 mx-4 my-2' />
          </>
        )}

        <Linkable
          className='w-full desktop:hidden'
          to={canManage ? ROUTES.MESSAGES.GROUPS.SETTINGS.$.ADMIN.INFO.$path({ relative: true }) : undefined}>
          <AppCell
            className='desktop:hidden'
            label={t('conversation.setting.group.name')}
            content={groupInfo?.GroupData?.GroupName ?? ''}
            chevron={!isDesktop && canManage}
          />
        </Linkable>

        <GroupAvatarUploadButton
          canManage={canManage}
          showIcon={false}
          groupId={groupId}>
          <AppCell
            className='desktop:hidden'
            label={t('conversation.setting.group.group-avatar')}
            content={
              <SafeImage
                src={groupInfo?.GroupData?.GroupImg}
                size={[40, 40]}
                className='size-10 rounded-full bg-cover'
              />
            }
            chevron={!isDesktop && canManage}
          />
        </GroupAvatarUploadButton>

        {!isChannel && (
          <>
            <AppCell
              className='desktop:hidden'
              label={t('conversation.setting.group.id')}
              content={
                groupInfo?.GroupData?.GroupCode && (
                  <span className='font-mon flex items-center gap-2 h-12'>
                    {groupInfo.GroupData.GroupCode}

                    <CopyButton content={groupInfo.GroupData.GroupCode.toString()} />
                  </span>
                )
              }
              onClick={() =>
                groupInfo?.GroupData?.GroupCode && copyTextToClipboard(groupInfo?.GroupData?.GroupCode.toString())
              }
            />

            <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.ANNOUNCEMENT.$path({ relative: true })}>
              <AppCell
                label={t('conversation.setting.group.announcement')}
                chevron={!isDesktop}
              />
            </Link>

            <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.QR_CODE.$path({ relative: true })}>
              <AppCell
                label={t('conversation.setting.group.qr-code.title')}
                chevron={!isDesktop}
              />
            </Link>

            <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.NICKNAME.$path({ relative: true })}>
              <AppCell
                label={t('conversation.setting.group.group-nickname')}
                chevron={!isDesktop}
              />
            </Link>

            <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />
          </>
        )}

        {!isChannel && (
          <Link to={ROUTES.MESSAGES.GROUPS.MEDIA.$buildPath({ params: { id: groupId } })}>
            <AppCell
              chevron={!isDesktop}
              label={t('my-collection.media')}
            />
          </Link>
        )}

        <Link to={ROUTES.MESSAGES.GROUPS.SEARCH.$buildPath({ params: { id: groupId } })}>
          <AppCell
            label={t('conversation.setting.search')}
            chevron={!isDesktop}
          />
        </Link>

        <AppCell
          label={t('conversation.setting.clear-all')}
          onClick={handleClearMessages}
        />

        <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />

        <AppCell
          label={t('conversation.setting.mute')}
          suffix={
            <Toggle
              checked={groupSession?.IsNotDisturb ?? false}
              isLoading={isUpdatingDnd}
              onChange={(value) => setDoNotDisturb(value).then(() => refetch())}
            />
          }
        />

        <AppCell
          label={t('conversation.setting.pin')}
          suffix={
            <Toggle
              checked={groupSession?.IsTop ?? false}
              isLoading={isUpdatingSessionTop}
              onChange={(value) => updateSessionTop(value).then(() => refetch())}
            />
          }
        />

        {!isChannel && (
          <>
            <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />

            <Link to={ROUTES.MESSAGES.GROUPS.REPORT.$buildPath({ params: { id: groupId } })}>
              <AppCell
                label={t('conversation.setting.report')}
                chevron={!isDesktop}
              />
            </Link>

            <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />

            <AppCell
              labelClassName='text-red-500'
              label={isGroupOwner ? t('conversation.setting.group.disband') : t('conversation.setting.group.leave')}
              onClick={handleQuitGroup}
            />
          </>
        )}
      </div>
    </PageLayout>
  );
}
